// @/services/content/store/index.js
import { BaseContentStore } from './base';
import { FirstRunHandler } from './modules/first-run';
import { CarbonAccountingHandler } from './modules/carbon-accounting';
import { CarbonAccountingUpdateHandler } from './modules/carbon-accounting-update';
import { CarbonAccountingAddDrawerHandler } from './modules/carbon-accounting-add-drawer';
import { CarbonAccountingDistributionDrawerHandler } from './modules/carbon-accounting-distribution-drawer';
import { CarbonAccountingResultsHandler } from './modules/carbon-accounting-results';
import { CarbonAccountingChartsHandler } from './modules/carbon-accounting-charts';
import { SupplyChainManagementHandler } from './modules/supply-chain-management';
import { ScmSupplierDetailsHandler } from './modules/scm-supplier-details';
import { ScmReminderDialogHandler } from './modules/scm-reminder-dialog';
import { ScmInviteHandler } from './modules/scm-invite';
import { SeBatchInviteHandler } from './modules/se-batch-invite';
import { ScmTrialHandler } from './modules/scm-trial';
import { ScmExportHandler } from './modules/scm-export';
import { SeAnalyticsHandler } from './modules/se-analytics';
import { SelfAssessmentHandler } from './modules/self-assessment';
import { SelfAssessmentBSHandler } from './modules/self-assessment-bs';
import { SelfAssessmentBSSectionHandler } from './modules/self-assessment-bs-section';
import { SelfAssessmentBSQuestionsHandler } from './modules/self-assessment-bs-questions';
import { SelfAssessmentBSELearningHandler } from './modules/self-assessment-bs-elearning';
import { SelfAssessmentCOHandler } from './modules/self-assessment-co';
import { CustomerRequestsHandler } from './modules/customer-requests';
import { SidebarHandler } from './modules/sidebar';
import { SubmissionHandler } from './modules/submission';
import { UsersHandler } from './modules/users';
import { SettingsHandler } from './modules/settings';
import { CompanyProfileHandler } from './modules/company-profile';
import { RegistrationHandler } from './modules/registration';
import { InvitationHandler } from './modules/invitation';
import { IndustryListHandler } from './modules/industry-list'

/**
 * ContentStoreManager
 * 
 * Central content management system with rich features:
 * - Multilingual content delivery with fallback mechanisms
 * - Module-specific content handlers with optimized loading
 * - GraphQL preloading for SSR and performance optimization
 * - Intelligent module initialization and dependency management
 * - Centralized access to localized content across the application
 */
class ContentStoreManager extends BaseContentStore {
  /**
   * Initialize content store manager
   * Creates module-specific content handlers
   */
  constructor() {
    super();

    // Initialize handlers
    this.firstRunHandler = new FirstRunHandler(this);
    this.carbonAccountingHandler = new CarbonAccountingHandler(this);
    this.carbonAccountingUpdateHandler = new CarbonAccountingUpdateHandler(this);
    this.carbonAccountingAddDrawerHandler = new CarbonAccountingAddDrawerHandler(this);
    this.carbonAccountingDistributionDrawerHandler = new CarbonAccountingDistributionDrawerHandler(this);
    this.carbonAccountingResultsHandler = new CarbonAccountingResultsHandler(this);
    this.carbonAccountingChartsHandler = new CarbonAccountingChartsHandler(this);
    this.supplyChainHandler = new SupplyChainManagementHandler(this);
    this.scmSupplierDetailsHandler = new ScmSupplierDetailsHandler(this);
    this.scmReminderDialogHandler = new ScmReminderDialogHandler(this);
    this.scmInviteHandler = new ScmInviteHandler(this);
    this.seBatchInviteHandler = new SeBatchInviteHandler(this);
    this.scmTrialHandler = new ScmTrialHandler(this);
    this.scmExportHandler = new ScmExportHandler(this);
    this.seAnalyticsHandler = new SeAnalyticsHandler(this);
    this.selfAssessmentHandler = new SelfAssessmentHandler(this);
    this.selfAssessmentBSHandler = new SelfAssessmentBSHandler(this);
    this.selfAssessmentBSSectionHandler = new SelfAssessmentBSSectionHandler(this);
    this.selfAssessmentBSQuestionsHandler = new SelfAssessmentBSQuestionsHandler(this);
    this.selfAssessmentBSELearningHandler = new SelfAssessmentBSELearningHandler(this);
    this.selfAssessmentCOHandler = new SelfAssessmentCOHandler(this);
    this.customerRequestsHandler = new CustomerRequestsHandler(this);
    this.sidebarHandler = new SidebarHandler(this);
    this.submissionHandler = new SubmissionHandler(this);
    this.usersHandler = new UsersHandler(this);
    this.settingsHandler = new SettingsHandler(this);
    this.companyProfileHandler = new CompanyProfileHandler(this);
    this.registrationHandler = new RegistrationHandler(this);
    this.invitationHandler = new InvitationHandler(this);
    this.industryListHandler = new IndustryListHandler(this);
  }

  /**
   * Initialize the content store
   * Loads content from GraphQL and ensures all modules are ready
   * 
   * @returns {Promise<boolean>} Success status
   */
  async initialize() {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Initializing content store...');
      }

      // Set initializing flag to prevent early access
      this.state.initializing = true;

      // Load all content from GraphQL at startup
      const loaded = this.preloadFromGraphQL();

      // Debug check if chart content was loaded
      if (this.state.data.carbonAccountingCharts) {
        const chartLocales = Object.keys(this.state.data.carbonAccountingCharts);

        if (process.env.NODE_ENV === 'development') {
          console.log('CONTENT STORE INIT - Chart content loaded:',
            chartLocales.map(locale => ({
              locale,
              fieldCount: Object.keys(this.state.data.carbonAccountingCharts[locale] || {}).length,
              sampleField: this.state.data.carbonAccountingCharts[locale]?.totalEmissionsTitle
            }))
          );
        }

        // If no locales were found but the object exists, add a default placeholder
        if (chartLocales.length === 0) {
          console.warn('Chart content object exists but has no locales, creating empty placeholder');
          this.state.data.carbonAccountingCharts.en = {};
        }
      } else {
        console.error('CONTENT STORE INIT - Chart content missing completely!');
        // Initialize empty structure to prevent errors
        this.state.data.carbonAccountingCharts = { en: {} };
      }

      // Mark initialization as complete
      this.state.initialized = true;
      this.state.initializing = false;

      if (process.env.NODE_ENV === 'development') {
        console.log('Content store initialization complete, loaded =', loaded);
      }
      return loaded;
    } catch (error) {
      console.error('Store initialization error:', error);

      // Still mark as initialized to prevent hanging
      this.state.initialized = true;
      this.state.initializing = false;

      return false;
    }
  }

  /**
   * Get First Run content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getFirstRunContent(locale = 'en') {
    return this.firstRunHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingContent(locale = 'en') {
    return this.carbonAccountingHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting Update content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingUpdateContent(locale = 'en') {
    return this.carbonAccountingUpdateHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting Add Drawer content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingAddDrawerContent(locale = 'en') {
    return this.carbonAccountingAddDrawerHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting Distribution Drawer content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingDistributionDrawerContent(locale = 'en') {
    return this.carbonAccountingDistributionDrawerHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting Results content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingResultsContent(locale = 'en') {
    return this.carbonAccountingResultsHandler.getContent(locale);
  }

  /**
   * Get Carbon Accounting Charts content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCarbonAccountingChartsContent(locale = 'en') {
    // Get content from handler
    const content = this.carbonAccountingChartsHandler.getContent(locale);

    // Add debugging to see what's being returned
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Store] Returning chart content for ${locale}:`,
        content ? Object.keys(content).length : 'null');
    }

    return content;
  }

  /**
   * Get SCM Invite Drawer content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getScmInviteContent(locale = 'en') {
    return this.scmInviteHandler.getContent(locale);
  }

  /**
   * Get SE Batch Invite Dialog content for specific locale
   *
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSeBatchInviteContent(locale = 'en') {
    return this.seBatchInviteHandler.getContent(locale);
  }

  /**
   * Get SCM Trial Mode content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getScmTrialContent(locale = 'en') {
    return this.scmTrialHandler.getContent(locale);
  }

  /**
   * Get SCM Export content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getScmExportContent(locale = 'en') {
    return this.scmExportHandler.getContent(locale);
  }

  /**
   * Get Supply Chain Management content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSupplyChainManagementContent(locale = 'en') {
    return this.state.data.supplyChainManagement[locale] ||
      this.state.data.supplyChainManagement['en']; // Fallback to English
  }

  /**
   * Get SCM Supplier Details content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getScmSupplierDetailsContent(locale = 'en') {
    return this.scmSupplierDetailsHandler.getContent(locale);
  }

  /**
   * Get SCM Reminder Dialog content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getScmReminderDialogContent(locale = 'en') {
    return this.scmReminderDialogHandler.getContent(locale);
  }

  /**
   * Get SE Analytics & Filters content for specific locale
   *
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSeAnalyticsContent(locale = 'en') {
    return this.seAnalyticsHandler.getContent(locale);
  }

  /**
   * Get Self Assessment content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSelfAssessmentContent(locale = 'en') {
    return this.state.data.selfAssessment[locale] ||
      this.state.data.selfAssessment['en']; // Fallback to English
  }

  /**
   * Get Self Assessment Business Segments content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content object or null if not available
   */
  getSelfAssessmentBSContent(locale = 'en') {
    return this.selfAssessmentBSHandler.getContent(locale);
  }

  /**
   * Get Self Assessment Business Segments Section content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSelfAssessmentBSSectionContent(locale = 'en') {
    return this.selfAssessmentBSSectionHandler.getContent(locale);
  }

  /**
   * Get Self Assessment Business Segments Questions content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSelfAssessmentBSQuestionsContent(locale = 'en') {
    return this.selfAssessmentBSQuestionsHandler.getContent(locale);
  }

  /**
   * Get question help text by ID for specific locale 
   * 
   * @param {string} questionId - Question identifier (e.g., 'EE01')
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {string|null} Help text or null if not found
   */
  getQuestionHelpText(questionId, locale = 'en') {
    return this.selfAssessmentBSQuestionsHandler.getQuestionHelpText(questionId, locale);
  }

  /**
   * Get question text by ID for specific locale
   * 
   * @param {string} questionId - Question identifier (e.g., 'EE01') 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {string|null} Question text or null if not found
   */
  getQuestionText(questionId, locale = 'en') {
    return this.selfAssessmentBSQuestionsHandler.getQuestionText(questionId, locale);
  }

  /**
   * Get question short title by ID for specific locale
   * 
   * @param {string} questionId - Question identifier (e.g., 'EE01') 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {string|null} Short title or null if not found
   */
  getQuestionShortTitle(questionId, locale = 'en') {
    return this.selfAssessmentBSQuestionsHandler.getQuestionShortTitle(questionId, locale);
  }

  /**
   * Get Self Assessment Business Segments eLearning content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Array|null} Content array or null if not found
   */
  getSelfAssessmentBSELearningContent(locale = 'en') {
    return this.selfAssessmentBSELearningHandler.getContent(locale);
  }

  /**
   * Get Self Assessment Company Overview content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content object or null if not available
   */
  getSelfAssessmentCOContent(locale = 'en') {
    return this.selfAssessmentCOHandler.getContent(locale);
  }

  /**
   * Get Customer Requests content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCustomerRequestsContent(locale = 'en') {
    return this.state.data.customerRequests[locale] ||
      this.state.data.customerRequests['en']; // Fallback to English
  }

  /**
   * Get Sidebar content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSidebarContent(locale = 'en') {
    return this.state.data.sidebar[locale] ||
      this.state.data.sidebar['en']; // Fallback to English
  }

  /**
   * Get Submission Drawer content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSubmissionContent(locale = 'en') {
    return this.state.data.submission[locale] ||
      this.state.data.submission['en']; // Fallback to English
  }

  /**
   * Get User Management content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getUsersContent(locale = 'en') {
    return this.usersHandler.getContent(locale);
  }

  /**
   * Get Account Settings content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getSettingsContent(locale = 'en') {
    return this.settingsHandler.getContent(locale);
  }

  /**
   * Get Company Profile content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getCompanyProfileContent(locale = 'en') {
    return this.companyProfileHandler.getContent(locale);
  }

  /**
   * Get Registration content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getRegistrationContent(locale = 'en') {
    return this.registrationHandler.getContent(locale);
  }

  /**
   * Get Invitation content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getInvitationContent(locale = 'en') {
    return this.invitationHandler.getContent(locale);
  }

  /**
   * Get IndustryList content for specific locale
   * 
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getIndustryListContent(locale = 'en') {
    return this.industryListHandler.getContent(locale);
  }

  /**
   * Get Hotspot Sectors content for specific locale.
   *
   * The primary source is the GraphQL query in useContentHotspotSectors; this
   * getter backs that hook's fallback path. Returns null (never throws) when the
   * store holds no Hotspot Sectors content, so an empty locale degrades quietly
   * instead of crashing the caller.
   *
   * @param {string} locale - Locale code (e.g., 'en')
   * @returns {Object|null} Content or null if not found
   */
  getHotspotSectorsContent(locale = 'en') {
    return this.state.data.hotspotSectors?.[locale] ||
      this.state.data.hotspotSectors?.['en'] ||
      null;
  }
}

// Export singleton instance
export const contentStore = new ContentStoreManager();